export interface PathPoint {
  lng: number;
  lat: number;
}

export interface InterpolationResult {
  lng: number;
  lat: number;
  bearing: number;
}

export function computeBearing(p1: PathPoint, p2: PathPoint): number {
  const lat1 = (p1.lat * Math.PI) / 180;
  const lat2 = (p2.lat * Math.PI) / 180;
  const dLng = ((p2.lng - p1.lng) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

export function interpolateAlongPath(
  coords: [number, number][],
  progress: number,
): InterpolationResult {
  if (!coords.length) return { lng: 0, lat: 0, bearing: 0 };
  if (coords.length === 1) return { lng: coords[0][0], lat: coords[0][1], bearing: 0 };

  const clamped = Math.max(0, Math.min(1, progress));

  const distances: number[] = [];
  let totalDistance = 0;
  for (let i = 1; i < coords.length; i++) {
    const dx = coords[i][0] - coords[i - 1][0];
    const dy = coords[i][1] - coords[i - 1][1];
    const d = Math.sqrt(dx * dx + dy * dy);
    distances.push(d);
    totalDistance += d;
  }

  if (totalDistance === 0) return { lng: coords[0][0], lat: coords[0][1], bearing: 0 };

  const targetDistance = clamped * totalDistance;
  let accumulated = 0;

  for (let i = 0; i < distances.length; i++) {
    const segmentDistance = distances[i];
    if (accumulated + segmentDistance >= targetDistance) {
      const segmentProgress = segmentDistance === 0 ? 0 : (targetDistance - accumulated) / segmentDistance;
      const p1: PathPoint = { lng: coords[i][0], lat: coords[i][1] };
      const p2: PathPoint = { lng: coords[i + 1][0], lat: coords[i + 1][1] };
      const lng = p1.lng + (p2.lng - p1.lng) * segmentProgress;
      const lat = p1.lat + (p2.lat - p1.lat) * segmentProgress;
      const bearing = computeBearing(p1, p2);
      return { lng, lat, bearing };
    }
    accumulated += segmentDistance;
  }

  const last = coords[coords.length - 1];
  const prev = coords[coords.length - 2];
  return {
    lng: last[0],
    lat: last[1],
    bearing: computeBearing({ lng: prev[0], lat: prev[1] }, { lng: last[0], lat: last[1] }),
  };
}

export function extractPathSegment(
  coords: [number, number][],
  startProgress: number,
  endProgress: number,
): [number, number][] {
  if (!coords.length) return [];
  if (coords.length === 1) return [coords[0]];

  const start = Math.max(0, Math.min(1, Math.min(startProgress, endProgress)));
  const end = Math.max(0, Math.min(1, Math.max(startProgress, endProgress)));
  if (start === end) return [];

  const distances: number[] = [0];
  let totalDistance = 0;
  for (let i = 1; i < coords.length; i++) {
    const dx = coords[i][0] - coords[i - 1][0];
    const dy = coords[i][1] - coords[i - 1][1];
    totalDistance += Math.sqrt(dx * dx + dy * dy);
    distances.push(totalDistance);
  }

  if (totalDistance === 0) return [coords[0]];

  const startDist = start * totalDistance;
  const endDist = end * totalDistance;
  const segment: [number, number][] = [];

  const startPoint = interpolateAlongPath(coords, start);
  segment.push([startPoint.lng, startPoint.lat]);

  for (let i = 1; i < coords.length - 1; i++) {
    if (distances[i] > startDist && distances[i] < endDist) {
      segment.push(coords[i]);
    }
  }

  const endPoint = interpolateAlongPath(coords, end);
  segment.push([endPoint.lng, endPoint.lat]);

  return segment;
}
